import { Alert, Badge, Card, Table } from 'react-bootstrap'
import { Address, Props } from '../../types/transparenzregister-ch.type'
import { t } from '../../locale'

const ROLE_OR_RELATIONSHIP_LABELS: Record<string, string> = {
  VORSITZENDER_DER_GESCHAEFTSFUEHRUNG: 'CEO / Managing Director',
  VERWALTUNGSRATS_ODER_VERWALTUNGSPRAESIDENT: 'Board President',
  LIQUIDATOR: 'Liquidator',
  SACHWALTER: 'Administrator',
  NOMINATOR: 'Nominator',
  TRUST_SETTLOR: 'Trust Settlor',
  TRUST_TRUSTEE: 'Trust Trustee',
  TRUST_BENEFICIARY: 'Trust Beneficiary',
  TRUST_PROTECTOR: 'Trust Protector',
  OTHER: 'Other',
}

const RELATIONSHIP_LABELS: Record<string, string> = {
  BO_ORDINARY: 'Beneficial owner',
  BO_INDIRECT_WITH_CHAIN: 'Indirect beneficial owner with control chain',
  BO_INDIRECT_WITHOUT_CHAIN: 'Indirect beneficial owner',
  BO_SUBSIDIARY: 'Subsidiary beneficial owner',
  CONTROL_CHAIN: 'Control chain',
  RESPONDENT: 'Respondent',
  AUTHORIZED_REPRESENTATIVE: 'Authorized representative',
}

const CONTROL_TYPE_LABELS: Record<string, string> = {
  SHAREHOLDING_VOTING_RIGHTS: 'Shareholding / voting rights',
  AAW_APPOINTMENT_OF_BOARD: 'Right to appoint board members',
  AAW_VETO_RIGHTS: 'Veto rights',
  AAW_DECISION_RIGHTS_OVER_PROFIT_OR_ASSET_TRANSFERS:
    'Decision rights over profits or asset transfers',
  AAW_RIGHTS_GRANTED_BY_CONTRACT: 'Control rights granted by contract',
  AAW_DEBT_INSTRUMENTS: 'Control through debt instruments',
  AAW_CONTROL_OVER_STATUTES: 'Control through statutes',
  AAW_CONTROL_VIA_LEGAL_REPRESENTATIVE: 'Control through legal representative',
  AAW_RELATIONSHIP_BETWEEN_RELATED_PARTIES: 'Control through related parties',
  AAW_CONTROL_VIA_FIDUCIARY: 'Control through fiduciary arrangement',
  OF_TRUST: 'Control through trust',
  OTHER: 'Other control type',
}

const SHARE_RANGE_LABELS: Record<string, string> = {
  BETWEEN_25_TO_50: '25–50%',
  GREATER_50_TO_75: '>50–75%',
  GREATER_75_TO_100: '>75–100%',
}

const ADDRESS_TYPE_LABELS: Record<string, string> = {
  FIRMENSITZ: 'Registered office',
  WOHNSITZ: 'Residence',
  POSTADRESSE_SCHWEIZ: 'Swiss postal address',
  BEVOLLMAECHTIGTER: 'Authorized representative',
  UNBEKANNT: 'Unknown',
}

const LEGAL_FORM_LABELS: Record<string, string> = {
  '0101': 'Sole proprietorship',
  '0103': 'General partnership (GP)',
  '0104': 'Limited partnership (LP)',
  '0105': 'Corporation with unlimited partners (CUP)',
  '0106': 'Corporation (Ltd)',
  '0107': 'Limited liability company (LLC)',
  '0108': 'Cooperative',
  '0109': 'Association',
  '0110': 'Foundation',
  '0111': 'Branch of a foreign company',
  '0113': 'Special legal form',
  '0114': 'Limited Partnership for collective investment schemes (LPCI)',
  '0115': 'Limited Partnership for collective investment schemes with a variable capital (SICAV)',
  '0116': 'Limited Partnership for collective investment schemes with a fixed capital (SICAF)',
  '0117': 'Public-law institution',
  '0118': 'Non-commercial power of attorney',
  '0119': 'Representative of the community of property',
  '0151': 'Branch',
  '0220': 'Administrative unit of the Confederation',
  '0221': 'Administrative unit of the canton',
  '0222': 'Administrative unit of the district',
  '0223': 'Administrative unit of the commune',
  '0224': 'Other public-law administrative unit',
  '0230': 'Public-law institution of the Confederation',
  '0231': 'Public-law institution of the canton',
  '0232': 'Public-law institution of the district',
  '0233': 'Public-law institution of the commune',
  '0234': 'Other public-law institution',
  '0302': 'Simple partnership',
  '0312': 'Permanent establishment or swiss representation of a foreign company',
  '0327': 'Foreign public company',
  '0328': 'Foreign public administration',
  '0329': 'International organisation',
  '0355': 'Other cooperative',
  '0361': 'Trust',
  '0362': 'Fund',
  '0441': 'Foreign company',
  '0571': 'Legal form undefined or unknown',
}

function formatCode(value?: string): string {
  if (!value) return '-'

  return value
    .toLowerCase()
    .replaceAll('_', ' ')
    .replace(/\b\w/g, character => character.toUpperCase())
}

function relationshipLabel(value?: string): string {
  if (!value) return '-'
  return RELATIONSHIP_LABELS[value] ?? formatCode(value)
}

function controlTypeLabel(value?: string): string {
  if (!value) return '-'
  return CONTROL_TYPE_LABELS[value] ?? formatCode(value)
}

function shareRangeLabel(value?: string): string {
  if (!value) return '-'
  return SHARE_RANGE_LABELS[value] ?? formatCode(value)
}

function legalFormLabel(legalForm?: string, declaredLegalForm?: string): string {
  if (declaredLegalForm) {
    return declaredLegalForm
  }

  if (!legalForm) {
    return '-'
  }

  const label = LEGAL_FORM_LABELS[legalForm]

  return label ? `${label} (${legalForm})` : legalForm
}

function addressTypeLabel(value?: string): string {
  if (!value) return ''
  return ADDRESS_TYPE_LABELS[value] ?? formatCode(value)
}

function formatAddress(address?: Address): string {
  if (!address) return '-'

  const street = [address.street, address.number].filter(Boolean).join(' ')

  const city = [address.postalCode, address.city].filter(Boolean).join(' ')

  return [street, city, address.countryCode].filter(Boolean).join(', ') || '-'
}

function stateVariant(state?: string): 'success' | 'warning' | 'secondary' | 'danger' {
  switch (state) {
    case 'ACTIVE':
      return 'success'

    case 'DELETED':
      return 'danger'

    case 'SUMMONS_OPEN':
    case 'DECLARATION_RECEIVED':
    case 'PRE_EXAMINATION_KS':
    case 'CONTROL_PROCESS_KS':
      return 'warning'

    default:
      return 'secondary'
  }
}

const normalizeUid = (u: string) => u.replace(/[^A-Z0-9]/gi, '').toUpperCase()

export default function TransparenzChResult({ data, clientName, clientUid }: Props) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return (
      <div className='text-center py-4'>
        <p className='text-muted'>{t('transparenzChNoRecords')}</p>
      </div>
    )
  }

  const records = Array.isArray(data.boRecords) ? data.boRecords : []

  const allEntityRecords = records.flatMap(r =>
    Array.isArray(r.recordStatements)
      ? r.recordStatements.filter(s => s.entityRecord).map(s => s.entityRecord!)
      : []
  )

  const matchedEntity = clientUid
    ? allEntityRecords.find(e => e.uid && normalizeUid(e.uid) === normalizeUid(clientUid))
    : undefined

  if (records.length === 0) {
    const unmatchedUids = Array.isArray(data.unmatchedUids) ? data.unmatchedUids : []

    return (
      <div className='text-center py-4'>
        <p className='text-muted mb-1'>{t('transparenzChNoRecords')}</p>

        {unmatchedUids.length > 0 && (
          <small className='text-muted'>
            {t('transparenzChUnmatchedUid')}: {unmatchedUids.join(', ')}
          </small>
        )}
      </div>
    )
  }

  return (
    <div>
      {records.map((record, recordIndex) => {
        const statements = Array.isArray(record.recordStatements) ? record.recordStatements : []

        const entityStatements = statements.filter(statement => statement.entityRecord)

        const personStatements = statements.filter(statement => statement.personRecord)

        const relationshipStatements = statements.filter(statement => statement.relationshipRecord)

        const mainEntity =
          entityStatements.find(statement => {
            const entityId = statement.id

            return statements.some(item => item.relationshipRecord?.subject === entityId)
          })?.entityRecord ?? entityStatements[0]?.entityRecord

        const annotations = Array.isArray(record.annotations) ? record.annotations : []

        return (
          <Card className='mb-4' key={recordIndex}>
            <Card.Header className='d-flex justify-content-between align-items-center'>
              <strong className='fs-6'>{mainEntity?.name ?? 'Company'}</strong>

              <Badge bg={stateVariant(record.entityState)}>{record.entityState ?? 'UNKNOWN'}</Badge>
            </Card.Header>

            <Card.Body>
              {(() => {
                if (recordIndex !== 0) return null
                if (!clientName && !clientUid) return null
                const rows: { field: string; local: string; register: string; match: boolean }[] =
                  []
                if (clientName) {
                  const registerName = (matchedEntity ?? mainEntity)?.name ?? ''
                  rows.push({
                    field: t('transparenzChName'),
                    local: clientName,
                    register: registerName,
                    match: registerName.toLowerCase().trim() === clientName.toLowerCase().trim(),
                  })
                }
                if (clientUid) {
                  const uidMatch = !!matchedEntity
                  const registerUid = matchedEntity?.uid ?? mainEntity?.uid ?? ''
                  rows.push({
                    field: t('transparenzChUid'),
                    local: clientUid,
                    register: registerUid,
                    match: uidMatch || normalizeUid(registerUid) === normalizeUid(clientUid),
                  })
                }
                const allMatch = rows.every(r => r.match)
                return (
                  <div className='mb-4'>
                    <h6 className='fw-semibold mb-2'>{t('transparenzChCompareTitle')}</h6>
                    <Alert variant={allMatch ? 'success' : 'warning'} className='py-2 px-3 mb-2'>
                      {allMatch
                        ? t('transparenzChCompareAllMatch')
                        : t('transparenzChCompareMismatch')}
                    </Alert>
                    <Table bordered size='sm' responsive>
                      <thead>
                        <tr>
                          <th>{t('transparenzChCompareField')}</th>
                          <th>{t('transparenzChCompareLocal')}</th>
                          <th>{t('transparenzChCompareRegister')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, i) => (
                          <tr key={i} className={row.match ? '' : 'table-warning'}>
                            <td className='text-muted'>{row.field}</td>
                            <td>{row.local}</td>
                            <td>
                              {row.register || '-'}
                              {!row.match && row.register && (
                                <i className='bi bi-exclamation-triangle-fill text-warning ms-2' />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )
              })()}

              <div className='d-flex flex-wrap gap-3 mb-4 small'>
                <div>
                  <strong>{t('transparenzChRecordsFound')}:</strong>{' '}
                  {data.nbOfRecordsFound ?? records.length}
                </div>

                <div>
                  <strong>{t('transparenzChPersons')}:</strong> {personStatements.length}
                </div>

                <div>
                  <strong>{t('transparenzChEntities')}:</strong> {entityStatements.length}
                </div>

                <div>
                  <strong>{t('transparenzChRelationships')}:</strong>{' '}
                  {relationshipStatements.length}
                </div>
              </div>

              <h6 className='fw-semibold mb-3'>{t('transparenzChCompanyInfo')}</h6>

              <Table bordered size='sm' responsive>
                <tbody>
                  <tr>
                    <th className='text-muted w-25'>{t('transparenzChName')}</th>
                    <td>{mainEntity?.name ?? '-'}</td>
                  </tr>

                  <tr>
                    <th className='text-muted'>{t('transparenzChUid')}</th>
                    <td>{mainEntity?.uid ?? '-'}</td>
                  </tr>

                  <tr>
                    <th className='text-muted'>{t('transparenzChLegalForm')}</th>
                    <td>{legalFormLabel(mainEntity?.legalForm, mainEntity?.declaredLegalForm)}</td>
                  </tr>

                  <tr>
                    <th className='text-muted'>{t('transparenzChJurisdiction')}</th>
                    <td>{mainEntity?.jurisdiction ?? '-'}</td>
                  </tr>

                  <tr>
                    <th className='text-muted'>{t('transparenzChAddress')}</th>
                    <td>{formatAddress(mainEntity?.addresses?.[0])}</td>
                  </tr>

                  <tr>
                    <th className='text-muted'>{t('transparenzChValidFrom')}</th>
                    <td>{record.validFrom ?? '-'}</td>
                  </tr>

                  <tr>
                    <th className='text-muted'>{t('transparenzChRecordVersion')}</th>
                    <td>{record.recordVersion ?? '-'}</td>
                  </tr>

                  <tr>
                    <th className='text-muted'>{t('transparenzChSource')}</th>
                    <td>{formatCode(record.sourceDescription)}</td>
                  </tr>
                </tbody>
              </Table>

              {entityStatements.length > 1 && (
                <>
                  <h6 className='fw-semibold mt-4 mb-3'>{t('transparenzChControlChain')}</h6>

                  <Table bordered size='sm' responsive>
                    <thead>
                      <tr>
                        <th>{t('transparenzChName')}</th>
                        <th>{t('transparenzChUid')}</th>
                        <th>{t('transparenzChLegalForm')}</th>
                        <th>{t('transparenzChAddress')}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {entityStatements.map((statement, entityIndex) => {
                        const entity = statement.entityRecord

                        return (
                          <tr key={statement.id ?? entityIndex}>
                            <td>{entity?.name ?? '-'}</td>

                            <td>{entity?.uid ?? '-'}</td>

                            <td>{legalFormLabel(entity?.legalForm, entity?.declaredLegalForm)}</td>

                            <td>{formatAddress(entity?.addresses?.[0])}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </>
              )}

              <h6 className='fw-semibold mt-4 mb-3'>{t('transparenzChBeneficialOwners')}</h6>

              {personStatements.length === 0 ? (
                <p className='text-muted'>{t('transparenzChNoPersons')}</p>
              ) : (
                personStatements.map((statement, personIndex) => {
                  const person = statement.personRecord

                  const personRelationships = relationshipStatements.filter(relationship =>
                    relationship.relationshipRecord?.interestedParties?.includes(statement.id ?? '')
                  )

                  const primaryAddress = person?.addresses?.[0]

                  return (
                    <Card className='mb-3' key={statement.id ?? personIndex}>
                      <Card.Body>
                        <h6 className='mb-1'>
                          {[person?.givenNames, person?.familyName].filter(Boolean).join(' ') ||
                            'Unknown person'}
                        </h6>

                        {statement.beneficialOwnerId && (
                          <div className='small text-muted mb-3'>
                            BO ID: {statement.beneficialOwnerId}
                          </div>
                        )}

                        <Table bordered size='sm' responsive>
                          <tbody>
                            <tr>
                              <th className='text-muted w-25'>{t('transparenzChDateOfBirth')}</th>

                              <td>{person?.birthDate ?? '-'}</td>
                            </tr>

                            <tr>
                              <th className='text-muted'>{t('transparenzChNationalities')}</th>

                              <td>{person?.nationalities?.join(', ') || '-'}</td>
                            </tr>

                            {primaryAddress?.email ? (
                              <tr>
                                <th className='text-muted'>{t('transparenzChEmail')}</th>

                                <td>
                                  {primaryAddress.email}
                                  {primaryAddress.addressType && (
                                    <span className='text-muted ms-2'>
                                      ({addressTypeLabel(primaryAddress.addressType)})
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ) : (
                              <tr>
                                <th className='text-muted'>{t('transparenzChAddress')}</th>

                                <td>
                                  {formatAddress(primaryAddress)}

                                  {primaryAddress?.addressType && (
                                    <span className='text-muted ms-2'>
                                      ({addressTypeLabel(primaryAddress.addressType)})
                                    </span>
                                  )}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>

                        {personRelationships.length === 0 ? (
                          <div className='small text-muted'>
                            {t('transparenzChNoRelationships')}
                          </div>
                        ) : (
                          personRelationships.map((relationship, relationshipIndex) => {
                            const relation = relationship.relationshipRecord

                            return (
                              <div
                                className='border rounded p-3 mt-2'
                                key={relationship.id ?? relationshipIndex}
                              >
                                <div className='mb-1'>
                                  <strong>{t('transparenzChRelationship')}:</strong>{' '}
                                  {relationshipLabel(relation?.relationshipType)}
                                </div>

                                {relation?.controlType && (
                                  <div className='mb-1'>
                                    <strong>{t('transparenzChControlType')}:</strong>{' '}
                                    {controlTypeLabel(relation.controlType)}
                                  </div>
                                )}

                                {relation?.shareRange && (
                                  <div className='mb-1'>
                                    <strong>{t('transparenzChShareRange')}:</strong>{' '}
                                    {shareRangeLabel(relation.shareRange)}
                                  </div>
                                )}

                                {relation?.roleOrRelationship && (
                                  <div className='mb-1'>
                                    <strong>{t('transparenzChRole')}:</strong>{' '}
                                    {ROLE_OR_RELATIONSHIP_LABELS[relation.roleOrRelationship] ??
                                      formatCode(relation.roleOrRelationship)}
                                    {relation.otherRoleOrRelationship &&
                                      ` — ${relation.otherRoleOrRelationship}`}
                                  </div>
                                )}

                                {relation?.interestDetails && (
                                  <div>
                                    <strong>{t('transparenzChDetails')}:</strong>{' '}
                                    {relation.interestDetails}
                                  </div>
                                )}

                                {relation?.controlType === 'OTHER' && relation.otherControlType && (
                                  <div>
                                    <strong>{t('transparenzChControlDetails')}:</strong>{' '}
                                    {relation.otherControlType}
                                  </div>
                                )}
                              </div>
                            )
                          })
                        )}
                      </Card.Body>
                    </Card>
                  )
                })
              )}

              {annotations.length > 0 && (
                <>
                  <h6 className='fw-semibold mt-4 mb-3'>{t('transparenzChAnnotations')}</h6>

                  {annotations.map((annotation, annotationIndex) => (
                    <Alert variant='warning' key={annotation.annotationId ?? annotationIndex}>
                      <div>
                        <strong>{t('transparenzChAnnotationReason')}:</strong>{' '}
                        {formatCode(annotation.standardReasonCode)}
                      </div>

                      <div>
                        <strong>{t('transparenzChAnnotationStatus')}:</strong>{' '}
                        {formatCode(annotation.annotationState)}
                      </div>

                      {(annotation.externalReasonText || annotation.extendedReasonText) && (
                        <div className='mt-1'>
                          {annotation.externalReasonText ?? annotation.extendedReasonText}
                        </div>
                      )}
                    </Alert>
                  ))}
                </>
              )}

              <div className='mt-3 small text-muted'>
                {t('transparenzChUndisclosedBos')}: {record.undisclosedBosCount ?? 0}
                {' · '}
                {t('transparenzChUnsuccessfullyIdentified')}:{' '}
                {record.unsuccessfullyIdentifiedBosCount ?? 0}
              </div>
            </Card.Body>
          </Card>
        )
      })}
    </div>
  )
}
